
<template>
  <ul :class="{ 'list-disc': !hideOrGrouping }">
    <li
      v-for="(normalizeRequirement,ri) in normalizeRequirements"
      :key="ri"
      class="list-item"
    >
      <div
        v-for="(item,i) in normalizeRequirement.values"
        :key="i"
        class="pb-2 flex"
        data-test="dm-requirement"
      >
        <strong
          v-if="i!==0 && !hideOrGrouping"
          class="mr-2"
        >OR</strong>
        <div
          v-if="Array.isArray(item)"
          class="sub-item"
        >
          <div
            v-for="(subItem, subIndex) in item"
            :key="subIndex"
            :class="subIndex !== (item.length - 1) && 'mb-2'"
            data-test="dm-sub-requirement"
          >
            <strong
              v-if="subIndex !== 0 && !hideOrGrouping"
              class="mr-1"
              :class="i === 0 && 'ml-8'"
            >AND</strong>
            <template v-if="subItem.code && subItem.type !== 'generic'">
              <a
                :href="subItem.url || '#'"
                class="underline text-blue-600 hover:opacity-80 transition-all duration-200"
              >{{ subItem.code }}</a> -
            </template>
            <span
              v-if="subItem.type === 'courseSets'"
              class="underline text-blue-600 hover:opacity-80 transition-all duration-200 cursor-pointer"
              @click="selectedCourseSetId = subItem.id"
            >
              <BaseIcon
                icon="list-alt"
                class="mr-2"
              />{{ subItem.name || '-' }}
            </span>
            <span v-else>{{ subItem.name || '-' }}</span>
            <span class="ml-1">{{ subItem.credits? `(${subItem.credits} ${$localize('credit', 'short')})` : '' }}</span>
          </div>
        </div>
        <div
          v-else
          class="sub-item"
        >
          <template v-if="item.code && item.type !== 'generic'">
            <a
              v-if="item.url"
              :href="item.url"
              class="underline text-blue-600 hover:opacity-80 transition-all duration-200"
            >{{ item.code }}</a>
            <span v-else>{{ item.code }}</span>
            -&nbsp;
          </template>
          <span
            v-if="item.type === 'courseSets'"
            class="underline text-blue-600 hover:opacity-80 transition-all duration-200 cursor-pointer"
            @click="selectedCourseSetId = item.id"
          >
            <BaseIcon
              icon="list-alt"
              class="mr-2"
            />{{ item.name || '-' }}
          </span>
          <span v-else>{{ item.name || '-' }}</span>
          <span
            v-if="normalizeRequirement.actualCredits && ['courseSets','generic'].includes(item.type)"
            class="ml-2"
          >{{ `(${normalizeRequirement.actualCredits} ${$localize('credit', 'short')})` }}</span>
          <span
            v-else
            class="ml-2"
          >{{ item.credits? `(${item.credits} ${$localize('credit', 'short')})` : '' }}</span>
        </div>
      </div>
      <p
        v-if="normalizeRequirement.courseRequirementGroupFreeText"
        class="pb-2"
      >
        {{ freeTextLabel }}: {{ normalizeRequirement.courseRequirementGroupFreeText }}
      </p>
      <DegreeMapCourseSetModal
        v-if="selectedCourseSetId"
        :value="selectedCourseSetId"
        @close="selectedCourseSetId = null"
      />
    </li>
  </ul>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import { useContext } from '@nuxtjs/composition-api'
import DegreeMapCourseSetModal from './DegreeMapCourseSetModal'
import useDegreeMaps from '@/composables/useDegreeMaps'
import { decamelize } from '@/utils/generalUtils'
import { HIDE_DM_OR_GROUPING, HIDE_GENERIC_IN_REQUIREMENTS } from '@/constants/featureFlags'
import useFeatureFlags from '@/composables/useFeatureFlags'

export default {
  components: {
    DegreeMapCourseSetModal,
  },
  props: {
    requirements: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
    courseSets: {
      type: Array,
      default: () => [],
    },
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { root }) {
    const { $localize } = useContext()
    const selectedCourseSetId = ref(null)
    const {
      getCourseSetRequirementTableObject,
      getCourseRequirementTableObject,
      outputCreditRange,
      normalizeMinMax,
    } = useDegreeMaps(root, $localize)

    const { hideOrGrouping, hideGenericInRequirements } = useFeatureFlags({
      hideOrGrouping: HIDE_DM_OR_GROUPING,
      hideGenericInRequirements: HIDE_GENERIC_IN_REQUIREMENTS,
    })

    function normalizeRequirement(requirement) {
      return requirement.requirementSelect.map((requirement) => {
        if (requirement.type === 'courses' || requirement.type === 'course') {
          if (Array.isArray(requirement.value)) {
            return requirement.value.map((v) =>
              getCourseRequirementTableObject(v, props.courses)
            )
          }
          return getCourseRequirementTableObject(
            requirement.value,
            props.courses
          )
        } else if (requirement.type === 'courseSets') {
          return getCourseSetRequirementTableObject(
            requirement.value,
            props.courseSets
          )
        } else {
          const requirementTypeDisplay = hideGenericInRequirements && requirement.type === 'generic' ? '' : `(${decamelize(requirement.type)})`
          return {
            ...requirement,
            id: requirement.value,
            name: `${requirement.value} ${requirementTypeDisplay}`,
          }
        }
      })
    }
    const normalizeRequirements = computed(() => {
      return props.requirements
        .filter((r) => r.requirementSelect.length)
        .map((requirement) => ({
          actualCredits: requirement.actualCredits && outputCreditRange(normalizeMinMax(requirement.actualCredits)),
          values: normalizeRequirement(requirement),
          courseRequirementGroupFreeText:
            requirement.courseRequirementGroupFreeText,
        }))
    })

    const freeTextLabel = computed(() => {
      return props.config.fields?.courseRequirementGroupFreeText?.label || ''
    })

    return {
      selectedCourseSetId,
      getCourseSetRequirementTableObject,
      getCourseRequirementTableObject,
      normalizeRequirements,
      hideOrGrouping,
      freeTextLabel,
    }
  },
}
</script>
<style scoped>
ul {
  padding-left: 1.5rem;
}
.sub-item {
  width: 100%;
  margin-right: 12px;
}
.text-blue-600 {
  color: #1c64f2 !important;
}
</style>
